import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EventBubbleContext } from './EventBubble.constants.ts';
import { tEventBubbleContext, tEventBubbleHook } from './EventBubble.types.ts';

export const useEventBubble = (): tEventBubbleHook => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [replyMessages, setReplyMessages] = useState<string[]>([]);
  const [isReplying, setIsReplying] = useState<boolean>(false);

  const menuRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  // on click "Esc" turn isReplying to false
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsReplying(false);
        setReplyMessages([]);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isReplying]);

  const selectMessageToReply = useCallback((messageSelected: boolean, id: string) => {
    if (messageSelected) {
      setReplyMessages(replyMessages.filter((id) => id !== id));
    } else {
      setReplyMessages([...replyMessages, id]);
    }
  }, [replyMessages]);

  return useMemo(() => ({
    isHovered,
    setIsHovered,
    showMenu,
    setShowMenu,
    menuRef,
    replyMessages,
    setReplyMessages,
    isReplying,
    setIsReplying,
    selectMessageToReply,
  }), [isHovered, showMenu, replyMessages, isReplying, selectMessageToReply]);
};

export const useMessageBubbleContext = (): tEventBubbleContext =>
  useContext(EventBubbleContext);
